import { useContext, useEffect } from "react";
import { useRouter } from "next/router";

import { CheckoutContext } from "contexts/CheckoutProvider";
import { FEATURE_FLAG_SHOW_FALL25_COUPON } from "components/Membership/membershipFeatureFlags";
import {
  FALL25_COUPON_CODE,
  isCouponClaimedByQuery,
} from "components/Step/stepUtil";

/**
 * When `extCouponCode` is the Fall25 offer in the URL, ensure the promo is applied
 * via the checkout API (same as `MembershipStepFall25Promo`) and order/session match
 * the banner — refetch alone does not attach the coupon to the cart.
 */
export function useFall25ExtCouponSync() {
  const router = useRouter();
  const { addPromoCode, getOrderSummary, getCheckoutSession } =
    useContext(CheckoutContext);

  useEffect(() => {
    if (!FEATURE_FLAG_SHOW_FALL25_COUPON || !router.isReady) {
      return;
    }
    // URL-only “Claimed!” state is insufficient: CheckoutProvider’s extCoupon refetch
    // hydrates order summary/session but does not POST the promo like the user-facing claim flows.
    if (!isCouponClaimedByQuery(router.query, FALL25_COUPON_CODE)) {
      return;
    }

    let cancelled = false;

    void (async () => {
      // Mirror MembershipStepSelectPlan / reading-level dialog: cart must receive the code via API.
      await addPromoCode?.(FALL25_COUPON_CODE);
      if (cancelled) {
        return;
      }
      await getOrderSummary?.();
      if (cancelled) {
        return;
      }
      await getCheckoutSession?.();
    })();

    return () => {
      cancelled = true;
    };
    // Intentionally omit CheckoutContext function identities: they are not stable
    // across renders and would retrigger this effect every time.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync when URL / readiness changes
  }, [router.isReady, router.query.extCouponCode]);
}
